export type FeedbackTone = 'neutral' | 'positive' | 'warning';

interface PlannedEffortOptions {
  numBars?: number;
  includeEnduranceAsLong?: boolean;
  fallbackWhenNoIntent?: boolean;
}

export function generatePlannedEffortProfile(
  intent: string | null | undefined,
  options?: PlannedEffortOptions
): number[] {
  const numBars = options?.numBars ?? 10;
  const includeEnduranceAsLong = options?.includeEnduranceAsLong ?? true;
  const fallbackWhenNoIntent = options?.fallbackWhenNoIntent ?? true;

  if (!intent) {
    return fallbackWhenNoIntent
      ? Array.from({ length: numBars }, () => 3 + Math.random() * 2)
      : [];
  }

  const lower = intent.toLowerCase();

  if (lower.includes('interval') || lower.includes('vo2')) {
    return Array.from({ length: numBars }, (_, i) =>
      i % 2 === 0 ? 3 + Math.random() * 2 : 7 + Math.random() * 2
    );
  }

  if (lower.includes('tempo') || lower.includes('threshold')) {
    return Array.from({ length: numBars }, (_, i) => {
      if (i < 2) return 3 + Math.random() * 2;
      if (i >= numBars - 2) return 3 + Math.random() * 2;
      return 6 + Math.random() * 2;
    });
  }

  if (lower.includes('long') || (includeEnduranceAsLong && lower.includes('endurance'))) {
    return Array.from({ length: numBars }, (_, i) => {
      const progress = i / numBars;
      if (progress < 0.2) return 4 + progress * 5;
      if (progress > 0.8) return 5 + (1 - progress) * 3;
      return 5 + Math.random() * 1.5;
    });
  }

  return Array.from({ length: numBars }, () => 3 + Math.random() * 2);
}

interface ToneOptions {
  positiveKeywords?: string[];
  warningKeywords?: string[];
}

export function determineFeedbackTone(
  text: string | null | undefined,
  options?: ToneOptions
): FeedbackTone {
  if (!text) return 'neutral';

  const lower = text.toLowerCase();
  const positiveKeywords = options?.positiveKeywords ?? ['great', 'strong', 'good'];
  const warningKeywords = options?.warningKeywords ?? ['caution', 'monitor', 'careful'];

  if (positiveKeywords.some((kw) => lower.includes(kw))) {
    return 'positive';
  }
  if (warningKeywords.some((kw) => lower.includes(kw))) {
    return 'warning';
  }
  return 'neutral';
}
